import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, memo, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';

import type {
  ICommonTypeaheadProps,
  ISelectingStatusSelecting,
  ITypeaheadOnfilter,
} from '../GenericTypeahead/Typeahead.models';
import { Typeahead } from '../GenericTypeahead/Typeahead/Typeahead';
import type { ISalesCounterpartResultPanelOwnProps } from './SalesCounterpartResultPanel';
import { SalesCounterpartResultPanel } from './SalesCounterpartResultPanel';

export type ISalesCounterPartTypeaheadProps = ICommonTypeaheadProps<ISalesCounterpart> &
  ITypeaheadOnfilter<ISalesCounterpart> &
  ISalesCounterpartResultPanelOwnProps & {
    fromItemToLabel?: (salesCounterpart: ISalesCounterpart | null) => string;
  };

export const SalesCounterpartTypeahead: FC<ISalesCounterPartTypeaheadProps> = (
  props: ISalesCounterPartTypeaheadProps,
) => {
  const defaultFromItemToLabel = useCallback(
    (salesCounterpart: ISalesCounterpart | null) => convertSalesCounterpartToString(salesCounterpart, props.useBdrId),
    [props.useBdrId],
  );
  return (
    <Typeahead<ISalesCounterpart>
      {...props}
      convertItemToString={props.fromItemToLabel ?? defaultFromItemToLabel}
      areItemsEqual={areSalesCounterpartsEqual}
      isValidItem={isValidSales}
      minLength={3}
      loadSuggestionsOnFocus={props.loadSuggestionsOnFocus || false}
      id={props.id}
    >
      {(selecting: ISelectingStatusSelecting<ISalesCounterpart>) => (
        <SalesCounterpartResultPanel
          {...selecting}
          useBdrId={props.useBdrId}
          hideSalesCounterpartName={props.hideSalesCounterpartName}
        />
      )}
    </Typeahead>
  );
};

export const SalesCounterpartTypeaheadSkeleton: FC = () => {
  const wrapper = memo(() => <div />);
  return (
    <div className="typeahead-component form-control">
      <div className="input-group input-group-md ">
        <div className="input-group-text text-primary">
          <Skeleton width="1em" wrapper={wrapper} />
        </div>
        <div className="ms-2">
          <Skeleton width="10em" />
        </div>
      </div>
    </div>
  );
};

function isNullOrEmptySales(sales: ISalesCounterpart | null) {
  return !sales || !sales.id;
}

function isValidSales(sales: ISalesCounterpart) {
  return !isNullOrEmptySales(sales);
}

export function getEliotCode(salesCounterpart: ISalesCounterpart): string {
  return salesCounterpart.eliotCode ? salesCounterpart.eliotCode : salesCounterpart.id;
}

export function convertSalesCounterpartToString(
  salesCounterpart: ISalesCounterpart | null,
  useBdrId: boolean = true,
): string {
  return salesCounterpart?.id
    ? useBdrId
      ? `${salesCounterpart.label} (${salesCounterpart.id})`
      : getEliotCode(salesCounterpart)
    : '';
}

export function areSalesCounterpartsEqual(sales1: ISalesCounterpart | null, sales2: ISalesCounterpart | null): boolean {
  const sales1Id = sales1?.id;
  const sales2Id = sales2?.id;

  return sales1Id === sales2Id;
}
